/**
 * shared/hull_custody.ts — CLIENT MIRROR of server/src/lib/hull_custody.ts (the server copy is
 * authoritative; keep them byte-identical below the headers — `verify:space-ledger` diffs them).
 */
import { HOLD_UNITS_PER_CARGO } from './space_industry';

// ── THE LOADED-HULL LAW (space campaign A, 2026-09-30) ──────────────────────────────────────────────
// A hull that carries ANY cargo_lot at ship:<id> — whoever owns it — cannot be broken up (scrap,
// reverse engineering): the hull is the goods' only place, and deleting it left the lots owned by no hull
// (space.md §14 gap 25). The pilot is the BAILEE of a port crate (h:<haulId>, the port's goods) and of a
// courier crate (c:<contractId>, taken on contract) — a bailee may not dispose of the goods it carries.
// Refuse, and say what is aboard and how to clear it. The destruction path is NOT this law: a destroyed
// hull's cargo follows THE WRECK LAW (destroyShipCore).

/** One lot aboard, pre-classified by the caller (the pure module never sees an Identity). */
export interface AboardLot { qty: number; tag: string; mine: boolean }

export interface AboardSummary {
  units: number;
  /** Untagged lots the pilot owns. */
  ownUnits: number;
  /** Port run crates (h:<haulId>) — the PORT's goods. */
  portUnits: number;
  portRuns: number;
  /** Courier crates (c:<contractId>) — distinct contracts. */
  courierUnits: number;
  courierCrates: number;
  /** Anything else (another owner's untagged lot — should not occur; counted, never hidden). */
  otherUnits: number;
  /** Survey relays (x:<siteKey>) — the pilot's own, bound for a survey desk (space campaign F). */
  relayUnits: number;
}

export function summarizeAboard(lots: readonly AboardLot[]): AboardSummary {
  const s: AboardSummary = { units: 0, ownUnits: 0, portUnits: 0, portRuns: 0, courierUnits: 0, courierCrates: 0, otherUnits: 0, relayUnits: 0 };
  const ports = new Set<string>(), crates = new Set<string>();
  for (const l of lots) {
    if (!(l.qty > 0)) continue;
    s.units += l.qty;
    if (l.tag.startsWith('h:')) { s.portUnits += l.qty; ports.add(l.tag); }
    else if (l.tag.startsWith('c:')) { s.courierUnits += l.qty; crates.add(l.tag); }
    else if (l.mine && l.tag === '') s.ownUnits += l.qty;
    else if (l.mine && l.tag.startsWith('x:')) s.relayUnits += l.qty;
    else s.otherUnits += l.qty;
  }
  s.portRuns = ports.size;
  s.courierCrates = crates.size;
  return s;
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** Why this hull cannot be broken up, naming what is aboard and how to clear each part; null when empty. */
export function scrapRefusal(s: AboardSummary, shipName: string, act: 'scrap' | 'reverse-engineer' = 'scrap'): string | null {
  if (s.units <= 0) return null;
  const parts: string[] = [];
  if (s.ownUnits > 0) parts.push(`${s.ownUnits} u of your own cargo (dock and unload it — Hold tab, or land it on the ground)`);
  if (s.portUnits > 0) parts.push(`${plural(s.portRuns, "port run's", "port runs'")} ${s.portUnits} u (the port's ore — land the run, or let it lapse and return)`);
  if (s.courierUnits > 0) parts.push(`${plural(s.courierCrates, 'courier crate', 'courier crates')} (${s.courierUnits} u, sealed — deliver at the destination)`);
  if (s.relayUnits > 0) parts.push(`${s.relayUnits} u of survey relay (deliver it at a survey desk, or hand it in there)`);
  if (s.otherUnits > 0) parts.push(`${s.otherUnits} u that belong to another owner (they leave only by that owner's contract)`);
  const verb = act === 'scrap' ? 'break up' : 'take apart';
  return `Cannot ${verb} ${shipName} — ${s.units} u aboard: ${parts.join('; ')}. Empty the hold first.`;
}

// ── THE HOLD LAW under a fit change (flag space_integrity_v1) ───────────────────────────────────────
/** The cargo component's bonus in cargo points (cargo_lots.holdCapacity's rule). */
export function cargoCompBonus(quality: number): number {
  return Math.round(Math.max(0, Math.min(1000, quality)) / 1000 * 20);
}
/** A hull's hold in units: (chassis cargo + the cargo comp's bonus) × HOLD_UNITS_PER_CARGO. */
export function holdUnits(chassisCargo: number, cargoCompQuality: number | null): number {
  return (chassisCargo + (cargoCompQuality === null ? 0 : cargoCompBonus(cargoCompQuality))) * HOLD_UNITS_PER_CARGO;
}
/** A fit change may not leave the hold holding more than it can (the goods would sit in an over-full hold). */
export function fitRefusal(capAfter: number, used: number, shipName: string): string | null {
  if (used <= capAfter) return null;
  return `${shipName}'s hold would shrink to ${capAfter} u with ${used} u aboard — unload ${used - capAfter} u first`;
}
